/**
 * SSEConnectionBanner — shows a non-blocking notice when the live SSE
 * stream is reconnecting after >2 retries. Renders nothing when the
 * stream is open.
 *
 * Touch target: the "Retry now" button does NOT carry its own
 * `min-h-[44px]` class — that would distort the shadcn button shape
 * (see `.claude/rules/switch-component-styling.md`). Touch area is
 * supplied by the parent row's `min-h-[44px]` padding.
 *
 * RTL: uses logical `ms-*` / `me-*` properties. The refresh icon mirrors
 * via `rtl:scale-x-[-1]`. No `rtl:flex-row-reverse` (native RTL handles
 * the layout).
 */
import type React from "react";
import type { SSEStatus } from "#/hooks/useSSE.js";
import type { TranslateFn } from "#/lib/i18n.js";

/** Inline icons — the web app does not depend on lucide-react. */
function WifiOffIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="2" y1="2" x2="22" y2="22" />
      <path d="M8.5 16.5a5 5 0 0 1 7 0" />
      <path d="M2 8.82a15 15 0 0 1 4.17-2.65" />
      <path d="M10.66 5c4.01-.36 8.14.9 11.34 3.76" />
      <path d="M16.85 11.25a10 10 0 0 1 2.22 1.68" />
      <path d="M5 13a10 10 0 0 1 5.24-2.76" />
      <line x1="12" y1="20" x2="12.01" y2="20" />
    </svg>
  );
}
function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4v6h-6" />
      <path d="M20.49 15A9 9 0 1 1 18.36 5.64L23 10" />
    </svg>
  );
}

export interface SSEConnectionBannerProps {
  status: SSEStatus;
  reconnectCount: number;
  onRetry: () => void;
  t: TranslateFn;
  /** Show the banner only once `reconnectCount` exceeds this. */
  showAfter?: number;
}

const DEFAULT_SHOW_AFTER = 2;

export function SSEConnectionBanner({
  status,
  reconnectCount,
  onRetry,
  t,
  showAfter = DEFAULT_SHOW_AFTER,
}: SSEConnectionBannerProps): React.ReactElement | null {
  // The banner is intentionally hidden during the very first reconnect
  // attempts — a 200 ms blip on a clean tab switch shouldn't flash UI.
  const shouldShow =
    (status === "reconnecting" || status === "paused") &&
    reconnectCount > showAfter;
  if (!shouldShow) return null;

  const message =
    status === "paused"
      ? t("connection:offline")
      : t("connection:reconnecting");

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-3 border-y border-border bg-muted/60 px-4 py-2 min-h-[44px] text-sm text-foreground"
    >
      <div className="flex items-center gap-2 text-muted-foreground">
        <WifiOffIcon className="h-4 w-4" />
        <span>{message}</span>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-2 text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors duration-150"
      >
        <RefreshIcon className="h-3.5 w-3.5 rtl:scale-x-[-1]" />
        {t("connection:retry")}
      </button>
    </div>
  );
}
